<template>
  <div data-testid="outreach-contact-list-actions">
    <BaseButton
      :aria-label="
        getLocaleText('outreach_contact_list_actions.edit_list_label', {
          name: contactList.name,
        })
      "
      color="secondary"
      data-testid="edit-list-button"
      dense
      flat
      icon="edit"
      new-design
      new-icon
      @click="onEditList"
    >
      <BaseStreemTooltip>
        {{ getLocaleText("outreach_contact_list_actions.edit_list_tooltip") }}
      </BaseStreemTooltip>
    </BaseButton>
    <BaseButton
      :aria-label="
        getLocaleText('outreach_contact_list_actions.duplicate_list_label', {
          name: contactList.name,
        })
      "
      color="secondary"
      data-testid="duplicate-list-button"
      dense
      flat
      icon="copy"
      new-design
      new-icon
      @click="onDuplicateList"
    >
      <BaseStreemTooltip>
        {{
          getLocaleText("outreach_contact_list_actions.duplicate_list_tooltip")
        }}
      </BaseStreemTooltip>
    </BaseButton>
    <BaseButton
      :aria-label="
        getLocaleText('outreach_contact_list_actions.delete_list_label', {
          name: contactList.name,
        })
      "
      color="secondary"
      data-testid="delete-list-button"
      dense
      flat
      icon="bin"
      new-design
      new-icon
      @click="onDeleteList"
    >
      <BaseStreemTooltip>
        {{ getLocaleText("outreach_contact_list_actions.delete_list_tooltip") }}
      </BaseStreemTooltip>
    </BaseButton>
  </div>
</template>

<script setup lang="ts">
import { Notify } from "quasar";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseStreemTooltip } from "shared/components/base";
import useTeamPicker from "shared/composables/useTeamPicker";
import DialogService from "shared/modals/DialogService";
import ModalService from "shared/modals/ModalService";
import { DialogTheme } from "shared/modals/types";
import type ContactList from "shared/resources/ContactList";
import features from "shared/services/features";

interface OutreachContactListActionsProps {
  contactList: ContactList;
}

const props = defineProps<OutreachContactListActionsProps>();

const emit = defineEmits<{
  action: [string];
}>();

const { selectedTeamPreference } = useTeamPicker();

function onEditList() {
  ModalService.open("OutreachContactListModal", {
    props: {
      contactList: props.contactList,
    },
    events: {
      save: () => {
        ModalService.close("OutreachContactListModal");

        emit("action", "resource-updated");
      },
    },
  });
}

async function duplicateContactList(newContactListName: string) {
  try {
    await $streemApiV1.post("contact_lists/duplicate", {
      params: {
        id: props.contactList.id,
        name: newContactListName,
        ...(features.has("has_team_workspaces")
          ? { organisation_team_id: selectedTeamPreference.value }
          : {}),
      },
    });

    Notify.create({
      message: getLocaleText(
        "outreach_contact_list_actions.duplicate_list_notification"
      ),
      color: "green",
    });

    emit("action", "duplicate-contact-list");
  } catch (error) {
    ErrorDialog(
      getLocaleText("outreach_contact_list_actions.duplicate_list_error"),
      error
    );
  }
}

function onDuplicateList() {
  ModalService.open("OutreachDuplicateContactListModal", {
    props: {
      contactListName: props.contactList.name,
    },
    events: {
      save: async (newContactListName: string) => {
        ModalService.close("OutreachDuplicateContactListModal");

        try {
          await duplicateContactList(newContactListName);
          emit("action", "resource-updated");
        } catch (error) {
          ErrorDialog(
            getLocaleText("outreach_contact_list_actions.save_list_error"),
            error
          );
        }
      },
    },
  });
}

async function deleteList() {
  try {
    await props.contactList.destroy();

    Notify.create({
      message: getLocaleText(
        "outreach_contact_list_actions.delete_list_notification"
      ),
      color: "green",
    });

    emit("action", "resource-updated");
  } catch (error) {
    ErrorDialog(
      getLocaleText("outreach_contact_list_actions.delete_list_error"),
      error
    );
  }
}

function onDeleteList() {
  DialogService.confirm({
    theme: DialogTheme.DANGER,
    title: getLocaleText(
      "outreach_contact_list_actions.delete_list_confirm_title"
    ),
    dialogMaxWidth: "350px",
    message: getLocaleText(
      "outreach_contact_list_actions.delete_list_confirm_message"
    ),
  }).onOk(() => {
    deleteList();
  });
}
</script>
